import Vue, { ref, computed, Ref, onMounted, onUnmounted } from 'vue';
import { useTask } from '@/ui/concerns';
import {
  ReportRecord,
  ReportRecordData as Report,
  ReportRecordData, ReportWidget,
} from '@/services/api/resources/analytics-builder/model';
import { create, get, update, remove, publicGet } from '@/services/api/resources/analytics-builder/actions';
import {
  get as getSchedule,
  create as createSchedule,
  update as updateSchedule,
} from '@/services/api/resources/analytics-scheduler/actions';
import { cloneDeep } from 'lodash/fp';
import omit from 'lodash/omit';
import { sequence, toasted } from '@/ui/helpers';
import { t } from '@/ui/i18n/i18n';
import { v4 as uuidv4 } from 'uuid';
import { Versioned, versioned } from '@/services/api';
import { user } from '@/ui/auth/auth';
import { router } from '@/ui/routes';
import { useReportWidget } from '@/ui/concerns/analytics/useReportRecord';
import {
  AllAttributesType,
  LineChartTimeGroupingType,
  MetricsType,
  WidgetDisplayOptions,
} from '@/services/api/resources/analytics-builder/constants';
import { useDisplay } from '@/ui/concerns/analytics/widget';
import { useFilterConfiguration } from '@/ui/concerns/analytics/useFilters';
import { debounce } from 'lodash';
import HtmlHelper from '@/ui/helpers/HtmlHelper';
import { chain, right } from 'fp-ts/lib/TaskEither';
import { pipe } from 'fp-ts/lib/function';
import { useScheduleStore } from '@/stores/analytics/schedule/schedule';
import { map } from 'fp-ts/TaskEither';
import { intoEditable } from '@/services/api/resources/analytics-scheduler';
import { retrieveApprovalStatuses } from '@/services/analytics/analyticsService';
import { useApprovalStatusesStore } from '@/stores/analytics/approvalStatuses';

const FIRST_LOAD_WIDGETS = 12;
const LAZY_LOAD_WIDGETS = 6;
const DEBOUNCE_WAIT = 100;
const BOTTOM_OFFSET = 375;

export const useAnalyticsActions = (
  reportId: Ref<number | undefined>,
  report: Ref<ReportRecordData>,
  reportPreviousState: Ref<ReportRecordData | undefined>,
  reportVersion: Ref<string>,
) => {
  const approvalStatusesStore = useApprovalStatusesStore();
  const RecordDataToFormReport = (data: ReportRecordData) => {
    const record = omit(data, ['user', 'createdAt', 'updatedAt']);

    return {
      ...record,
      widgets: record.widgets?.map(item => item.id === 0 ? omit(item, ['id']) : item),
    };
  };

  const createRecord = useTask(
    () => create(RecordDataToFormReport(report.value)),
    ({ id }) => {
      reportId.value = id;
      report.value.user = { id: user.value.id, name: user.value.name };
      syncReport.run();
      reportPreviousState.value = cloneDeep(report.value);
      toasted.success(t('analytics.report-created'));
    },
    uuidv4(),
    error => {
      toasted.error(error.message);
    },
  );

  const scheduleStore = useScheduleStore();

  const updateSchedulePipe = () => pipe(
    updateSchedule(scheduleStore.id!, versioned(scheduleStore.record, scheduleStore.version)),
    map(() => ({ id: scheduleStore.id! })),
  );

  const createSchedulePipe = () => pipe(
    createSchedule(scheduleStore.record),
    map(response => {
      report.value.schedule = response.id;

      return response;
    }),
  );

  const scheduleAction = () => report.value.schedule !== null
    ? updateSchedulePipe()
    : createSchedulePipe();

  const callScheduleAction = () => scheduleStore.record.assignees.length > 0
    ? scheduleAction()
    : right(null as never);

  const updateRecord = useTask(
    () => pipe(
      callScheduleAction(),
      chain(() => update(reportId.value!, versioned(RecordDataToFormReport(report.value), reportVersion.value))),
    ),
    () => {
      report.value.schedule ? syncReportWithSchedule.run() : syncReport.run();
      reportPreviousState.value = cloneDeep(report.value);
      toasted.success(t('analytics.report-updated'));
    },
    uuidv4(),
    error => {
      toasted.error(error.message);
    },
  );

  const deleteRecord = useTask(
    () => remove(reportId.value!),
    () => {
      toasted.success(t('analytics.report-deleted'));
      router.push({ name: 'analytics-v2' });
    },
    uuidv4(),
    error => {
      toasted.error(error.message);
    },
  );

  const getReportFn = (getFn: Function) => useTask(
    () => getFn(reportId.value!),
    ({ data, version }: Versioned<ReportRecord>) => {
      reportPreviousState.value = cloneDeep(data.data);
      report.value = data.data;
      reportVersion.value = version;
    },
  );

  const getReport = getReportFn(get);

  const getPublicReport = getReportFn(publicGet);

  const setReportValues = (widgets: ReportWidget[], version: string, obfuscatedId?: number) => {
    widgets.forEach(item => {
      const widget = report.value.widgets.find(widget => widget.key === item.key);
      if (widget) {
        widget.id = item.id;
        item.order = widget.order;
      }
    });
    Vue.set(report.value, 'obfuscatedId', obfuscatedId);
    reportPreviousState.value = cloneDeep(report.value);
    reportVersion.value = version;
  };

  const syncReportWithSchedule = useTask(
    () => sequence(
      get(reportId.value!),
      getSchedule(report.value.schedule!),
    ),
    ([
      reportRecord,
      scheduleRecord,
    ]) => {
      setReportValues(reportRecord.data.data.widgets, reportRecord.version, reportRecord.data.data.obfuscatedId);
      scheduleStore.setId(report.value.schedule!);
      scheduleStore.setRecord(intoEditable(scheduleRecord.data));
      scheduleStore.setVersion(scheduleRecord.version);
    },
  );

  const syncReport = useTask(
    () => get(reportId.value!),
    ({ data: { data: { widgets, obfuscatedId } }, version }: Versioned<ReportRecord>) =>
      setReportValues(widgets, version, obfuscatedId),
  );

  const getApprovalStatuses = (isPublic = false) => useTask(
    () => retrieveApprovalStatuses(isPublic),
    statusList => {
      approvalStatusesStore.addAll(statusList.data);
    },
    uuidv4(),
    e => console.error(e),
  );

  return {
    createRecord,
    updateRecord,
    deleteRecord,
    getReport,
    getPublicReport,
    syncReport,
    syncReportWithSchedule,
    getApprovalStatuses,
  };
};

export const useAnalyticsBuilder = (report: Ref<Report>) => {
  const { getDisabledOptionalFilters, containsDisabledFilters, getOnlyActiveFilters } = useFilterConfiguration();
  const htmlHelper = new HtmlHelper();
  const maxVisibleWidgets = ref(FIRST_LOAD_WIDGETS);
  const visibleWidgets = computed(() => report.value.widgets.slice(0, maxVisibleWidgets.value));

  const findWidget = (key: string) => report.value.widgets?.find(item => item.key === key);

  const onMetricChanged = (key: string, metric: MetricsType | null) => {
    const widget = findWidget(key);
    if (widget) {
      widget.metric = metric;
      const disabled = getDisabledOptionalFilters(metric);

      if (containsDisabledFilters(widget.filters, disabled)) {
        widget.filters = getOnlyActiveFilters(widget.filters, disabled);
      }
    }
  };

  const onAttributeChanged = (key: string, attribute: AllAttributesType | null) => {
    const widget = findWidget(key);
    if (widget) {
      widget.attribute = attribute;
    }
  };

  const onAddWidgetClicked = () => {
    if (!report.value.widgets) {
      report.value.widgets = [];
    }

    report.value.widgets.push(useReportWidget(report.value.widgets.length));
    showMoreWidgetsWhenAdding();
  };

  const onPercentageToggled = (key: string) => {
    const widget = findWidget(key);
    if (widget) {
      widget.percentage = !widget.percentage;
    }
  };

  const onDisplayChange = (key: string, display: WidgetDisplayOptions) => {
    const widget = findWidget(key);
    if (widget) {
      const { displayOptionsArray } = useDisplay();
      displayOptionsArray().forEach(item => {
        widget[item.value] = item.value === display;
      });
    }
  };

  const onLineChartTimeGroupingChange = (key: string, value: LineChartTimeGroupingType) => {
    const widget = findWidget(key);
    if (widget) {
      widget.lineChartTimeGrouping = value;
    }
  };

  const onDeleteCard = (key: string) => {
    const index = report.value.widgets!.findIndex(item => item.key === key);
    if (index !== -1) {
      report.value.widgets!.splice(index, 1);
    }

    reorderWidgets();
  };

  const onAddCardAfter = (key: string) => {
    const index = report.value.widgets!.findIndex(item => item.key === key);
    if (index > -1) {
      report.value.widgets!.splice(index + 1, 0, useReportWidget(index + 1));
    }

    reorderWidgets();
    showMoreWidgetsWhenAdding();
  };

  const reorderWidgets = () => report.value.widgets?.forEach((item, i) => {
    item.order = i;
  });

  const onTitleChange = (title: string) => {
    report.value.title = title;
  };

  const resetVisibleWidgets = () => {
    maxVisibleWidgets.value = FIRST_LOAD_WIDGETS;
  };

  const showMoreWidgetsWhenAdding = () => {
    if (report.value.widgets.length > maxVisibleWidgets.value) {
      maxVisibleWidgets.value += LAZY_LOAD_WIDGETS;
    }
  };

  const pageScrolled = () => {
    const isAtBottom = htmlHelper.isAtWindowBottom(BOTTOM_OFFSET);

    if (isAtBottom && maxVisibleWidgets.value < report.value.widgets.length) {
      maxVisibleWidgets.value += LAZY_LOAD_WIDGETS;
    }
  };

  const pageScrollDebounced = debounce(pageScrolled, DEBOUNCE_WAIT);

  onMounted(() => {
    window.addEventListener('scroll', pageScrollDebounced);
  });

  onUnmounted(() => {
    window.removeEventListener('scroll', pageScrollDebounced);
  });

  return {
    onMetricChanged,
    onAttributeChanged,
    onAddWidgetClicked,
    onPercentageToggled,
    onDisplayChange,
    onLineChartTimeGroupingChange,
    onDeleteCard,
    onAddCardAfter,
    onTitleChange,
    visibleWidgets,
    resetVisibleWidgets,
  };
};
